/**
 * src/services/watchlistEnforcement.ts
 *
 * Post-connect compliance evaluation.  Callers (typically App.tsx or the
 * wallet-connect hook) invoke `evaluateWalletAtLogin(wallet, country)`
 * immediately after a smart-account/wallet is connected.  The function
 * returns a structured decision the UI can act on:
 *
 *   - BLOCKED      → wallet must be logged out; show a persistent banner.
 *   - WARN         → wallet may continue; show a non-dismissable warning.
 *   - CLEAR        → no action.
 *
 * Uses `effectiveStatus` from entitiesService which proxies the off-chain
 * list mirror.  The on-chain list contract remains the source of truth.
 */

import { effectiveStatus, LIST_STATUS } from './entitiesService';

export type WatchlistDecision = 'BLOCKED' | 'WARN' | 'CLEAR';

export interface WatchlistEvaluation {
  decision: WatchlistDecision;
  rawStatus: number;
  humanReason: string;
  scope: 'global' | 'country' | null;
}

export async function evaluateWalletAtLogin(wallet: string, country?: number): Promise<WatchlistEvaluation> {
  try {
    const status = await effectiveStatus(wallet, country);
    switch (status) {
      case LIST_STATUS.DENY_GLOBAL:
        return { decision: 'BLOCKED', rawStatus: status, humanReason: 'Wallet is globally denied.', scope: 'global' };
      case LIST_STATUS.DENY_COUNTRY:
        return { decision: 'BLOCKED', rawStatus: status, humanReason: 'Wallet is denied in this country.', scope: 'country' };
      case LIST_STATUS.WATCH_GLOBAL:
        return { decision: 'WARN', rawStatus: status, humanReason: 'Wallet is on the global watchlist.', scope: 'global' };
      case LIST_STATUS.WATCH_COUNTRY:
        return { decision: 'WARN', rawStatus: status, humanReason: 'Wallet is on this country’s watchlist.', scope: 'country' };
      default:
        return { decision: 'CLEAR', rawStatus: status, humanReason: 'No compliance action required.', scope: null };
    }
  } catch (err) {
    // Conservative default: on network/auth errors we DO NOT block — the
    // user would otherwise be unable to recover.  We still surface the
    // diagnostic so the audit trail records the attempt.
    return {
      decision: 'CLEAR',
      rawStatus: LIST_STATUS.CLEAR,
      humanReason: `Compliance check failed: ${(err as Error).message}`,
      scope: null,
    };
  }
}
